import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from './cn';
import { Button } from './Button';
import { isAppError } from '@/lib/api/errors';

/**
 * The three states every asynchronous surface in the console can be in, plus the
 * wrapper that picks between them.
 *
 * The one rule these enforce at the TYPE level: a state cannot be rendered
 * without copy. `EmptyState` used to take `title` with an optional description,
 * so a bare "No results" heading — a dead end that tells a user
 * nothing about why the list is empty or what to do next — compiled. It no
 * longer does: `title` and `body` are both required, and `LoadingState` requires
 * the label a screen reader announces. If a screen has nothing to say in one of
 * these states, that is a content gap to close, not a prop to omit.
 */

/** Skeleton shimmer for loading lists/detail. */
export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-sm bg-canvas-deep-sand', className)} />;
}

export interface LoadingStateProps {
  /**
   * What is being fetched, in the app's words ("Loading articles…"). Required:
   * this is the only thing a screen-reader user gets while the skeleton renders,
   * and `aria-busy` alone announces nothing.
   */
  label: string;
  /** Skeleton shape. `rows` for a table or list, `block` for a detail pane. */
  variant?: 'rows' | 'block';
  /** Row count for the `rows` variant. */
  rows?: number;
  className?: string;
}

export function LoadingState({ label, variant = 'rows', rows = 6, className }: LoadingStateProps) {
  return (
    <div
      className={cn('flex flex-col gap-xs', className)}
      role="status"
      aria-busy
      aria-live="polite"
      data-testid="loading-state"
    >
      {/* Visible to assistive tech only: the skeleton already carries the visual
          message, and a duplicated "Loading…" line under it is noise. */}
      <span className="sr-only">{label}</span>
      {variant === 'rows' ? (
        Array.from({ length: rows }).map((_, i) => <Skeleton key={i} className="h-[44px] w-full" />)
      ) : (
        <>
          <Skeleton className="h-[28px] w-1/3" />
          <Skeleton className="h-[16px] w-2/3" />
          <Skeleton className="h-[220px] w-full" />
        </>
      )}
    </div>
  );
}

export interface EmptyStateProps {
  /** What is not here — "No articles yet", not "No results". */
  title: string;
  /**
   * Why it is empty and what to do about it. Required: an empty state with a
   * heading and nothing else is a dead end, and it is the state a new user is
   * most likely to meet first.
   */
  body: string;
  /** The way out — usually the primary action that would create the first record. */
  action?: ReactNode;
  /**
   * Extra structure between the body and the action — a short list of what the
   * thing is for, a note about who can create one. For the cases where one
   * sentence genuinely is not enough; the API-tokens screen is the reason this
   * exists, because "no tokens yet" without saying what a token *is* sends the
   * reader to the documentation rather than to the button.
   */
  details?: ReactNode;
  /** Overrides the eyebrow above the title, for a state that is not "nothing yet"
   *  (e.g. a filtered list that matched nothing). */
  eyebrow?: string;
  className?: string;
  /**
   * Where this block sits in the document outline; `2` by default, because a
   * state is nearly always the whole of what a screen or a tab has to say and
   * sits directly under its `<h1>`. Pass `3` when it stands inside a section
   * that already carries a heading of its own. Skipping a level is a WCAG 1.3.1
   * failure that only a screen-reader user meets.
   */
  headingLevel?: 2 | 3;
}

export function EmptyState({
  title,
  body,
  action,
  details,
  eyebrow,
  className,
  headingLevel = 2,
}: EmptyStateProps) {
  const { t } = useTranslation();
  const Heading = `h${headingLevel}` as 'h2' | 'h3';
  return (
    <div
      data-testid="empty-state"
      className={cn(
        'flex flex-col items-center gap-sm rounded-md border-[0.5px] border-dashed border-hairline-strong bg-canvas-bone p-3xl text-center',
        className,
      )}
    >
      <p className="eyebrow text-ink-mute">{eyebrow ?? t('ui.nothingHereYet')}</p>
      <Heading className="text-title text-ink-deep">{title}</Heading>
      <p className="max-w-md text-small text-ink-mid">{body}</p>
      {details}
      {action}
    </div>
  );
}

export interface ErrorStateProps {
  /** The thrown value. Supplies the technical detail and the `request_id`. */
  error: unknown;
  /**
   * What failed, in the app's words ("Couldn't load articles"). Required: the
   * message on the error itself is written for whoever debugs it, not for
   * whoever is looking at the screen.
   */
  title: string;
  onRetry?: () => void;
  className?: string;
  /**
   * Where this block sits in the document outline; `2` by default, because a
   * state is nearly always the whole of what a screen or a tab has to say and
   * sits directly under its `<h1>`. Pass `3` when it stands inside a section
   * that already carries a heading of its own. Skipping a level is a WCAG 1.3.1
   * failure that only a screen-reader user meets.
   */
  headingLevel?: 2 | 3;
}

export function ErrorState({
  error,
  title,
  onRetry,
  className,
  headingLevel = 2,
}: ErrorStateProps) {
  const { t } = useTranslation();
  const Heading = `h${headingLevel}` as 'h2' | 'h3';
  const requestId = isAppError(error) ? error.requestId : undefined;
  const message = error instanceof Error ? error.message : t('ui.somethingWentWrong');
  return (
    <div
      role="alert"
      data-testid="error-state"
      className={cn(
        'flex flex-col items-center gap-sm rounded-md border-[0.5px] border-signal-error/40 bg-signal-error-fill p-2xl text-center',
        className,
      )}
    >
      <p className="eyebrow text-signal-error">{t('ui.error')}</p>
      <Heading className="text-component-title text-ink-deep">{title}</Heading>
      <p className="text-small text-ink-graphite">{message}</p>
      {onRetry && (
        <Button variant="ghost" onClick={onRetry}>
          {t('common.retry')}
        </Button>
      )}
      {requestId && (
        <details className="mt-xs">
          <summary className="cursor-pointer text-label text-ink-mute">{t('ui.reportThis')}</summary>
          <code className="tnum mt-2xs block select-all font-mono text-label text-ink-mute">
            request_id: {requestId}
          </code>
        </details>
      )}
    </div>
  );
}

export function ForbiddenBanner({ message }: { message?: string }) {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      className="mb-md rounded-md border-s-[3px] border-signal-amber border-[0.5px] border-hairline bg-signal-amber-fill px-md py-sm text-small text-signal-amber-ink"
    >
      {message ?? t('ui.readOnlyAccess')}
    </div>
  );
}

/**
 * Standard four-state wrapper (spec §5.6): every list/detail composes this so
 * loading, empty, error, and forbidden are handled uniformly.
 *
 * **`copy` is required, and so is the empty-state wording** (PHASE 12 decision). A
 * wrapper that quietly supplied "No results" and "Loading" on the caller's behalf is how
 * a console ends up with dead-end states: a state a user can reach has to be written, in
 * the words of the thing being fetched, before it compiles.
 *
 * The empty branch is only reachable when `isEmpty` is given, so the type only
 * demands empty copy then — and `empty` (a bespoke `EmptyState`, usually one
 * carrying a next action) satisfies it instead.
 */
interface DataViewQuery<T> {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  data: T | undefined;
  refetch: () => void;
}

interface DataViewBase<T> {
  query: DataViewQuery<T>;
  forbidden?: boolean;
  children: (data: T) => ReactNode;
}

/** Words for the states this wrapper renders on the screen's behalf. */
export interface StateCopy {
  /** Announced while the skeleton is up — "Loading articles…". */
  loading: string;
  /** What failed, in the app's words — "Couldn't load articles". */
  errorTitle: string;
}

export interface EmptyCopy extends StateCopy {
  /** What is not here — "No articles yet", never "No results". */
  emptyTitle: string;
  /** Why, and what to do about it. */
  emptyBody: string;
}

export type DataViewProps<T> = DataViewBase<T> &
  (
    | { isEmpty?: undefined; empty?: undefined; copy: StateCopy }
    | { isEmpty: (data: T) => boolean; empty: ReactNode; copy: StateCopy }
    | { isEmpty: (data: T) => boolean; empty?: undefined; copy: EmptyCopy }
  );

export function DataView<T>(props: DataViewProps<T>) {
  const { query, forbidden, children } = props;
  const { isEmpty, empty } = props;
  // Narrowed by the union above: `emptyTitle`/`emptyBody` are present exactly
  // when the empty branch is reachable and no bespoke `empty` node was given.
  const copy = props.copy as EmptyCopy;

  if (forbidden) return <ForbiddenBanner />;
  if (query.isLoading) return <LoadingState label={copy.loading} />;
  if (query.isError) {
    // A 403 surfaced as an error → read-only banner rather than a scary error card.
    if (isAppError(query.error) && query.error.is('forbidden')) return <ForbiddenBanner />;
    return <ErrorState error={query.error} title={copy.errorTitle} onRetry={query.refetch} />;
  }
  if (query.data === undefined) return <LoadingState label={copy.loading} />;
  if (isEmpty?.(query.data)) {
    return <>{empty ?? <EmptyState title={copy.emptyTitle} body={copy.emptyBody} />}</>;
  }
  return <>{children(query.data)}</>;
}
